import ClassNames from "Helpers/Common";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useTraffic } from "CustomHooks/useConversions";

// Site traffic, first-party.
//
// This replaced Beam Analytics — the vendor dissolved, its collector stopped
// answering, and every pageview had been firing into a dead endpoint. Rather
// than swap in another vendor, it reads the same landing-impression
// collection the Impressions column reads. The conversions funnel filters it
// to ad arrivals (hasQuery); this does not filter it at all.
//
// TWO DIFFERENT NUMBERS, on purpose:
//   pageviews — every hit, including reloads and returns to a page.
//   visitors  — distinct people, from the ic_vid cookie.
// A row is one visitor-page-day carrying a view counter, so a visitor who
// refreshes forty times costs one document and still reports forty views.

const nf = new Intl.NumberFormat();

const Stat = ({ label, value, sub }) => (
  <div className="rounded-md border border-gray-300 shadow-sm bg-white px-4 py-3 flex-1 min-w-[150px]">
    <div className="text-xs bali_hai">{label}</div>
    <div className="text-xl font-semibold text-black mt-0.5">{nf.format(value ?? 0)}</div>
    {sub && <div className="text-xs bali_hai mt-0.5">{sub}</div>}
  </div>
);

// One breakdown table. `emptyKeyLabel` names the null bucket explicitly —
// "(direct)" for a referrer, "(unknown)" elsewhere — because a blank cell in
// an analytics table reads as a rendering bug rather than as real data.
const Breakdown = ({ title, table, emptyKeyLabel = "(unknown)", keyLabel = "Name", total }) => {
  const rows = table?.rows ?? [];
  return (
    <div className="rounded-md border border-gray-300 shadow-sm bg-white p-4 flex-1 min-w-[320px]">
      <div className="flex items-baseline justify-between mb-2">
        <span className="text-xs font-semibold text-black">{title}</span>
        {table?.truncated && (
          <span className="text-xs bali_hai" title="More rows exist than are shown here.">
            top {rows.length} only
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="text-sm bali_hai py-2">No data in this range</div>
      ) : (
        <table className="w-full">
          <thead>
            <tr className="text-xs bali_hai text-left">
              <th className="font-medium pb-1">{keyLabel}</th>
              <th className="font-medium pb-1 w-20 text-right">Views</th>
              <th className="font-medium pb-1 w-20 text-right">Visitors</th>
              <th className="font-medium pb-1 w-14 text-right">Share</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={String(r.key)} className="border-t border-gray-100">
                <td className="py-1.5 text-sm truncate max-w-0" title={r.key || emptyKeyLabel}>
                  {r.key || <span className="bali_hai">{emptyKeyLabel}</span>}
                </td>
                <td className="py-1.5 text-sm text-right">{nf.format(r.pageviews ?? 0)}</td>
                <td className="py-1.5 text-sm text-right">{nf.format(r.visitors ?? 0)}</td>
                <td className="py-1.5 text-xs bali_hai text-right">
                  {total ? `${((r.pageviews / total) * 100).toFixed(1)}%` : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export const TrafficPanel = ({ enabled, from, to }) => {
  const { data, isLoading, isFetching, isError } = useTraffic({ from, to, enabled });

  if (!enabled) return null;
  if (isError) return <div className="mt-4 text-sm text-red-600">Could not load the traffic report.</div>;
  if (isLoading || !data) return <div className="mt-4 text-sm bali_hai">Loading traffic…</div>;

  const t = data.totals || {};
  const views = t.pageviews || 0;
  const organic = views - (t.paidPageviews || 0);
  const perVisitor = t.visitors ? (views / t.visitors).toFixed(1) : "0.0";

  return (
    // keepPreviousData means isLoading is false on every refetch after the
    // first, so the panel would sit on the previous range's numbers with no
    // sign a request is running. Dim while fetching, same as the tables.
    <div className={ClassNames("mt-4 flex flex-col gap-4", isFetching && "opacity-90 transition-opacity")}>
      <div className="flex flex-wrap gap-3">
        <Stat label="Pageviews" value={views} sub={`${perVisitor} per visitor`} />
        <Stat label="Unique visitors" value={t.visitors} sub="distinct ic_vid cookies" />
        <Stat
          label="From ads"
          value={t.paidPageviews}
          sub={views ? `${(((t.paidPageviews || 0) / views) * 100).toFixed(1)}% of views` : "—"}
        />
        <Stat
          label="Organic / direct"
          value={organic}
          sub={views ? `${((organic / views) * 100).toFixed(1)}% of views` : "—"}
        />
      </div>

      {data.byDay?.rows?.length > 0 && (
        <div className="rounded-md border border-gray-300 shadow-sm bg-white p-4">
          <div className="text-xs font-semibold text-black mb-3">Pageviews per day</div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={data.byDay.rows} margin={{ top: 4, right: 8, bottom: 0, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef1f4" vertical={false} />
              <XAxis dataKey="key" tick={{ fontSize: 11, fill: "#8fa2b7" }} tickLine={false} axisLine={false} />
              <YAxis tick={{ fontSize: 11, fill: "#8fa2b7" }} tickLine={false} axisLine={false} allowDecimals={false} />
              <Tooltip
                formatter={(v, name) => [nf.format(v), name === "pageviews" ? "Pageviews" : "Visitors"]}
                contentStyle={{ fontSize: 12, borderRadius: 6, border: "1px solid #e5e7eb" }}
              />
              <Area type="monotone" dataKey="pageviews" stroke="#2f9e9e" fill="#2f9e9e" fillOpacity={0.12} strokeWidth={2} />
              <Area type="monotone" dataKey="visitors" stroke="#8fa2b7" fill="#8fa2b7" fillOpacity={0.08} strokeWidth={1.5} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      <div className="flex flex-wrap gap-4">
        <Breakdown title="Top pages" table={data.byPath} keyLabel="Path" emptyKeyLabel="(unknown)" total={views} />
        <Breakdown title="Referrers" table={data.byReferrer} keyLabel="Source" emptyKeyLabel="(direct / none)" total={views} />
      </div>
      <div className="flex flex-wrap gap-4">
        <Breakdown title="Channels" table={data.byChannel} keyLabel="Channel" total={views} />
        <Breakdown title="Sites" table={data.byHost} keyLabel="Host" total={views} />
      </div>

      <div className="text-xs bali_hai">
        Counted first-party from the marketing site and the AI checker. A visitor with cookies blocked is not
        counted at all — the id is what makes a repeat visit one person rather than several — so these numbers
        are a floor, not a ceiling. Nothing here identifies anyone: a random cookie id, a path, and the ad
        params already stored on a pre-registration.
      </div>
    </div>
  );
};

export default TrafficPanel;
